// src/lib/permissions/components/PermissionGuard.tsx
// Declarative permission-based rendering component

import React from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { ModuleCategory, AccessLevel } from '../core/modules';

export interface PermissionGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  
  // Permission-based access
  permission?: string;
  permissions?: string[];
  permissionMode?: 'any' | 'all';
  
  // Module-based access
  module?: string;
  moduleAction?: 'access' | 'view' | 'update';
  
  // Admin access
  requireAdmin?: boolean;
  
  // Category access
  category?: ModuleCategory;
  
  // Custom validation
  customCheck?: () => boolean;
  
  // Loading state
  showLoadingFallback?: boolean;
}

/**
 * Permission Guard Component
 * Declarative way to show/hide content based on permissions
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  fallback = null,
  permission,
  permissions,
  permissionMode = 'any',
  module,
  moduleAction = 'access',
  requireAdmin = false,
  category,
  customCheck,
  showLoadingFallback = false
}) => {
  const permissionApi = usePermissions();  
  // Show loading fallback if requested and loading
  if (showLoadingFallback && permissionApi.loading) {
    return <>{fallback}</>;
  }
  
  // If no groups or error, deny access
  if (permissionApi.noGroups || permissionApi.error) {
    return <>{fallback}</>;
  }

  let hasAccess = false;

  try {
    // Custom check has highest priority
    if (customCheck) {
      hasAccess = customCheck();
    }
    // Admin requirement check
    else if (requireAdmin) {
      hasAccess = permissionApi.hasAdminAccess();
    }
    // Multiple permissions check
    else if (permissions && permissions.length > 0) {
      hasAccess = permissionMode === 'all'
        ? permissionApi.hasAllPermissions(permissions)
        : permissionApi.hasAnyPermission(permissions);
    }
    // Single permission check
    else if (permission) {
      hasAccess = permissionApi.hasPermission(permission);
    }
    // Module-based check
    else if (module) {
      switch (moduleAction) {
        case 'view':
          hasAccess = permissionApi.canViewModule(module);
          break;
        case 'update':
          hasAccess = permissionApi.canUpdateModule(module);
          break;
        case 'access':
        default:
          hasAccess = permissionApi.canAccessModule(module);
          break;
      }
    }
    // Category-based check
    else if (category) {
      const categoryModules = permissionApi.getModulesByCategory(category);
      hasAccess = categoryModules.length > 0;
    }
    // Default to deny if no conditions specified
    else {
      hasAccess = false;
    }
  } catch (error) {
    console.error('PermissionGuard error:', error);
    hasAccess = false;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};
/**
 * Specialized guards for common use cases
 */

export const AdminGuard: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard requireAdmin={true} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const ModuleGuard: React.FC<{
  children: React.ReactNode;
  module: string;
  action?: 'access' | 'view' | 'update';
  fallback?: React.ReactNode;
}> = ({ children, module, action = 'access', fallback }) => (
  <PermissionGuard module={module} moduleAction={action} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const DashboardGuard: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard module="dashboard" fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const CalendarGuard: React.FC<{
  children: React.ReactNode;
  action?: 'view' | 'update';
  fallback?: React.ReactNode;
}> = ({ children, action = 'view', fallback }) => (
  <PermissionGuard module="calendar" moduleAction={action} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const Visualization2DGuard: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard permission="2d_visualisation_access" fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const Visualization3DGuard: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
  <PermissionGuard permission="3d_visualisation_access" fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const VisualizationGuard: React.FC<{
  children: React.ReactNode;
  type?: '2d' | '3d' | 'any';
  fallback?: React.ReactNode;
}> = ({ children, type = 'any', fallback }) => {
  if (type === '2d') {
    return (
      <PermissionGuard permission="2d_visualisation_access" fallback={fallback}>
        {children}
      </PermissionGuard>
    );
  }
  
  if (type === '3d') {
    return (
      <PermissionGuard permission="3d_visualisation_access" fallback={fallback}>
        {children}
      </PermissionGuard>
    );
  }
  
  // 'any' - user needs either 2D or 3D access
  return (
    <PermissionGuard 
      permissions={['2d_visualisation_access', '3d_visualisation_access']} 
      permissionMode="any" 
      fallback={fallback}
    >
      {children}
    </PermissionGuard>
  );
};

/**
 * Higher-order component for permission checking
 */
export const withPermissions = <P extends object>(
  Component: React.ComponentType<P>,
  permissionProps: Omit<PermissionGuardProps, 'children' | 'fallback'>
) => {
  return (props: P & { fallback?: React.ReactNode }) => {
    const { fallback, ...componentProps } = props;
    
    return (
      <PermissionGuard {...permissionProps} fallback={fallback}>
        <Component {...(componentProps as P)} />
      </PermissionGuard>
    );
  };
};
/**
 * Hook for conditional rendering based on permissions
 */
export const useConditionalRender = () => {
  const permissions = usePermissions();
  
  return {
    /**
     * Render content if user has permission
     */
    ifHasPermission: (permission: string, content: React.ReactNode) => 
      permissions.hasPermission(permission) ? content : null,
      
    /**
     * Render content if user can access module
     */
    ifCanAccessModule: (module: string, content: React.ReactNode) =>
      permissions.canAccessModule(module) ? content : null,
      
    /**
     * Render content if user is admin
     */
    ifAdmin: (content: React.ReactNode) =>
      permissions.hasAdminAccess() ? content : null,
      
    /**
     * Render different content based on access level
     */
    switchByAccessLevel: (
      module: string, 
      content: {
        none?: React.ReactNode;
        view?: React.ReactNode;
        update?: React.ReactNode;
      }
    ) => {
      const level = permissions.getModuleAccessLevel(module);
      return content[level] || content.none || null;
    }
  };
};